import { takeEvery, takeLatest, debounce, call, put, all, select } from 'redux-saga/effects';
import { prop, omit, pick } from 'ramda';

import api from 'api';
import { withAlert } from 'store/alerts';

import {
  ID,
  MESSAGE,
  FILES,
  META,
  OWNER,
  EDITOR,
  SUB_PATIENT,
  EXPAND,
  FIELDS,
  FILE_ID,
  FILE_URL,
  FILE_URL_EXPIRES,
  BUCKET,
  MIMETYPE,
} from '.';
import { updateInfoItems, updateInfoItem, deleteInfoItem, updatePagination } from './actions';
import {
  FETCH_INFO_ITEMS,
  FETCH_INFO_ITEM,
  CREATE_INFO_ITEM,
  SAVE_INFO_ITEM,
  REMOVE_INFO_ITEM,
  UPLOAD_MEDIA,
  LOAD_FILE,
  DELETE_FILE,
} from './types';
import { getInfoItemProp } from './selectors';

const query = {
  [FIELDS]: [MESSAGE, FILES, META, SUB_PATIENT],
  [EXPAND]: [`${META}.${OWNER}`, `${META}.${EDITOR}`],
};

function* fetchInfoItems({ payload }) {
  const data = yield call(api.getInfoItems, payload);
  yield put(updateInfoItems(data.data));
  yield put(updatePagination(omit(['data'], data)));
}

function* fetchInfoItem({ payload }) {
  const data = yield call(api.getInfoItem, payload, query);
  yield put(updateInfoItem(data));
}

function* createInfoItem({ payload }) {
  const data = yield call(api.postInfoItem, payload, query);
  yield put(updateInfoItem(data));
}

function* saveInfoItem({ payload }) {
  const data = yield call(api.patchInfoItem, prop(ID, payload), payload, query);
  yield put(updateInfoItem(data));
}

function* removeInfoItem({ payload }) {
  const files = yield select(getInfoItemProp(payload)(FILES));

  yield all(files.map((file) => call(api.deleteFileFromGCloud, file)));

  const data = yield call(api.deleteInfoItem, payload);

  yield put(deleteInfoItem(data));
}

function* uploadMedia({ payload }) {
  const data = yield all(payload.map((file) => call(api.uploadGCloudFile, file, 'info_item')));

  return { success: data.filter(Boolean) };
}

function* loadFile({ payload }) {
  return { success: yield call(api.loadFile, pick([FILE_ID, FILE_URL, FILE_URL_EXPIRES, BUCKET, MIMETYPE], payload)) };
}

function* deleteFile({ payload }) {
  yield call(api.deleteFileFromGCloud, payload);

  return { success: payload.id };
}

export default function* watchInfoItems() {
  yield takeLatest(FETCH_INFO_ITEMS, withAlert(fetchInfoItems));
  yield takeLatest(FETCH_INFO_ITEM, withAlert(fetchInfoItem));
  yield takeEvery(CREATE_INFO_ITEM, withAlert(createInfoItem));
  yield debounce(500, SAVE_INFO_ITEM, withAlert(saveInfoItem));
  yield takeEvery(REMOVE_INFO_ITEM, withAlert(removeInfoItem));
  yield takeEvery(UPLOAD_MEDIA, withAlert(uploadMedia));
  yield takeEvery(LOAD_FILE, withAlert(loadFile));
  yield takeEvery(DELETE_FILE, withAlert(deleteFile));
}
